import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { djs } from '@duke-hq/djs';
import PDFWrapper, { DukeFooterLogo } from '../PdfLayout';
import { pdfColors, sharedStyles, getOutcomeBadgeStyle } from './sharedStyles';
import AssessmentAuditReportProps from './types';
import OpenQuestionItem from './OpenQuestionItem';
import QuestionGroupItem from './QuestionGroupItem';
import FormSlideItem from './FormSlideItem';
import FileUploadPromptItem from './FileUploadPromptItem';
import Summary from './Summary';

const QuestionType = {
  OPEN_QUESTION: 'OPEN_QUESTION',
  QUESTION_GROUP: 'QUESTION_GROUP',
  FORM: 'FORM',
  FILE_UPLOAD_PROMPT: 'FILE_UPLOAD_PROMPT',
} as const;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
  },
  contentArea: {
    display: 'flex',
    flexGrow: 1,
  },
  header: {
    marginBottom: 20,
  },
  separator: {
    borderBottomWidth: 1,
    borderBottomColor: pdfColors.brandDark,
    marginBottom: 16,
  },
  sectionHeader: {
    fontSize: 14,
    fontFamily: 'Inter-SemiBold',
    marginBottom: 8,
    color: pdfColors.textStrong,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  label: {
    fontSize: 11,
    width: 120,
    color: pdfColors.textLabel,
  },
  value: {
    fontSize: 11,
    flex: 1,
    color: pdfColors.textStrong,
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  footerContent: {
    marginLeft: 16,
    marginTop: 12,
  },
  submissionItem: {
    marginTop: 16,
    padding: 16,
    backgroundColor: pdfColors.white,
    borderWidth: 1.5,
    borderColor: pdfColors.borderMuted,
    borderRadius: 8,
  },
  submissionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: pdfColors.border,
  },
  submissionTitle: {
    fontSize: 13,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.text,
    letterSpacing: 0.3,
  },
  submissionDetailsSection: {
    backgroundColor: pdfColors.background,
    padding: 12,
    borderRadius: 6,
    marginBottom: 12,
  },
  submissionInfoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  feedbackSection: {
    marginTop: 12,
    padding: 12,
    backgroundColor: pdfColors.warningBg,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: pdfColors.warning,
  },
  feedbackTitle: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.warningLabel,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  feedbackText: {
    fontSize: 10,
    color: pdfColors.text,
    lineHeight: 1.5,
  },
  questionsSection: {
    marginTop: 12,
  },
  submissionsSection: {
    marginTop: 8,
  },
  submissionsSectionHeader: {
    fontSize: 15,
    fontFamily: 'Inter-SemiBold',
    marginBottom: 4,
    color: pdfColors.textHeading,
  },
  footerLogoContainer: {
    marginTop: 10,
  },
});

function AssessmentReport({ data }: { data: AssessmentAuditReportProps }): React.ReactElement {
  return (
    <PDFWrapper title="Assessment Audit Report">
      <View style={styles.container}>
        <View style={styles.contentArea}>
          <View style={styles.header}>
            <View style={styles.separator} />

            {/* Student Information */}
            <Text style={styles.sectionHeader}>Student Information</Text>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Name:</Text>
              <Text style={styles.value}>{data.studentName}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Email:</Text>
              <Text style={styles.value}>{data.studentEmail}</Text>
            </View>

            {/* Unit Information */}
            <Text style={[styles.sectionHeader, { marginTop: 12 }]}>Unit of Competency</Text>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Unit Code:</Text>
              <Text style={styles.value}>{data.unitCode}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Unit Title:</Text>
              <Text style={styles.value}>{data.unitTitle}</Text>
            </View>
          </View>

          {/* Submissions Content */}
          <View style={styles.submissionsSection}>
            <Text style={styles.submissionsSectionHeader}>Assessment Submissions</Text>
            <View>
              {data.submissions.map((submission, index) => {
                const outcome = (submission.outcome || '').toUpperCase();
                const badgeStyle = getOutcomeBadgeStyle(outcome);

                return (
                  <View key={submission.id} style={styles.submissionItem} break={index > 0}>
                    <View style={styles.submissionHeader}>
                      <Text style={styles.submissionTitle}>
                        SUBMISSION {index + 1} (Revision{' '}
                        {submission.content?.[0]?.revisionNumber ?? 0})
                      </Text>
                      <View style={[sharedStyles.badgeBase, badgeStyle]}>
                        <Text style={sharedStyles.badgeText}>{outcome.replace(/_/g, ' ')}</Text>
                      </View>
                    </View>

                    <View style={styles.submissionDetailsSection}>
                      <View style={styles.submissionInfoRow}>
                        <Text style={styles.label}>Submitted date:</Text>
                        <Text style={styles.value}>
                          {submission.submittedAt != null
                            ? djs(submission.submittedAt).format('LLL')
                            : 'N/A'}
                        </Text>
                      </View>

                      <View style={styles.submissionInfoRow}>
                        <Text style={styles.label}>Assessed by:</Text>
                        <Text style={styles.value}>{submission.assessedByName || 'N/A'}</Text>
                      </View>

                      {submission.assessedAt != null ? (
                        <View style={styles.submissionInfoRow}>
                          <Text style={styles.label}>Assessment date:</Text>
                          <Text style={styles.value}>
                            {djs(submission.assessedAt).format('LLL')}
                          </Text>
                        </View>
                      ) : null}
                    </View>

                    {submission.assessmentFeedback != null ? (
                      <View style={styles.feedbackSection} wrap={false}>
                        <Text style={styles.feedbackTitle}>OVERALL FEEDBACK</Text>
                        <Text style={styles.feedbackText}>{submission.assessmentFeedback}</Text>
                      </View>
                    ) : null}

                    <View style={styles.questionsSection} break>
                      {submission.content
                        .filter(
                          (
                            item,
                          ): item is typeof item & {
                            question: NonNullable<typeof item.question>;
                          } =>
                            (item.questionType === QuestionType.OPEN_QUESTION ||
                              item.questionType === QuestionType.QUESTION_GROUP ||
                              item.questionType === QuestionType.FORM ||
                              item.questionType === QuestionType.FILE_UPLOAD_PROMPT) &&
                            item.question != null,
                        )
                        .map((item, idx) => {
                          if (item.questionType === QuestionType.OPEN_QUESTION) {
                            return (
                              <View key={item.originalAnswerId} break={idx > 0}>
                                <OpenQuestionItem
                                  question={item.question}
                                  userInput={item.userInput ?? ''}
                                  correct={item.correct}
                                  feedback={item.feedback}
                                />
                              </View>
                            );
                          }

                          if (
                            item.questionType === QuestionType.QUESTION_GROUP &&
                            item.question.questions &&
                            item.question.questions.length > 0
                          ) {
                            return (
                              <View key={item.originalAnswerId} break={idx > 0}>
                                <QuestionGroupItem
                                  questionGroup={{
                                    id: item.question.id,
                                    title: item.question.title,
                                    position: item.question.position,
                                    questions: item.question.questions,
                                  }}
                                  answerIds={item.answerId}
                                  correct={item.correct}
                                />
                              </View>
                            );
                          }

                          if (
                            item.questionType === QuestionType.FORM &&
                            item.question.sections != null &&
                            Array.isArray(item.question.sections) &&
                            item.question.sections.length > 0
                          ) {
                            return (
                              <View key={item.originalAnswerId} break={idx > 0}>
                                <FormSlideItem
                                  formSlide={{
                                    id: item.question.id,
                                    title: item.question.title,
                                    correct: item.correct,
                                    description: Array.isArray(item.question.description)
                                      ? item.question.description
                                      : [],
                                    hint: Array.isArray(item.question.hint)
                                      ? item.question.hint
                                      : null,
                                    position: item.question.position,
                                    sections: item.question.sections,
                                  }}
                                  feedback={item.feedback}
                                />
                              </View>
                            );
                          }

                          if (item.questionType === QuestionType.FILE_UPLOAD_PROMPT) {
                            return (
                              <View key={item.originalAnswerId} break={idx > 0}>
                                <FileUploadPromptItem
                                  question={item.question}
                                  userInput={item.userInput ?? ''}
                                  correct={item.correct}
                                  files={Array.isArray(item.files) ? item.files : []}
                                  feedback={item.feedback}
                                />
                              </View>
                            );
                          }

                          return null;
                        })}
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </View>

        {/* Assessment Summary */}
        <Summary submissions={data.submissions} />

        {/* Footer */}
        <View style={styles.footer} wrap={false}>
          <View style={styles.footerContent}>
            <View style={styles.footerLogoContainer}>
              <DukeFooterLogo />
            </View>
          </View>
        </View>
      </View>
    </PDFWrapper>
  );
}

export default AssessmentReport;
